import { generateClient } from 'aws-amplify/api';
import dayjs from 'dayjs';
import * as queries from '../graphql/queries';
import * as mutations from '../graphql/mutations';
import {
  iapSubscriptionProcessingDeferred,
  iapSubscriptionProcessingResumed,
} from '../features/iap/iap_slice';
import { trackEvents } from './tracking_operations';
import { UpdateMembershipResult } from '../API';
import { appSyncGraphQLApi } from '../app/smorgAppSyncGraphQLApi';
import { spaceMembershipWithBackendUpdateMembershipResult } from '../services/end_user_space_memberships';
import { postMessageToSmorgCompanion } from '../services/smorg_companion_auth';

const client = generateClient();

export const sendRequestFeatureNotificationOperation = async (
  userId,
  userEmail,
  featureCode,
) => {
  const response = await client.graphql({
    query: mutations.adminEmailUserRequestedFeature,
    variables: {
      userId,
      userEmail,
      featureCode,
    },
  });
  return response.data.adminEmailUserRequestedFeature;
};

export const sendAccountDeletedNotificationOperation = async (
  userId,
  userEmail,
) => {
  const response = await client.graphql({
    query: mutations.adminEmailUserDeleted,
    variables: {
      userId,
      userEmail,
    },
  });
  return response.data.adminEmailUserDeleted;
};

export const getIngredientTreeNodesOperation = async (locale) => {
  const response = await client.graphql({
    query: mutations.getIngredientTreeNodes,
    variables: { locale },
    authMode: 'iam',
  });
  return response.data.getIngredientTreeNodes;
};

export const createSimpleSpaceMembershipOperation = async (
  spaceID,
  onboardingAnswers,
  referrer,
) => {
  const doc = { spaceID, referrer };
  if (onboardingAnswers && onboardingAnswers.length > 0) {
    doc.onboardingAnswers = onboardingAnswers;
    doc.onboardingAnswersLastUpdatedAt = new Date().toISOString();
  }
  const response = await client.graphql({
    query: mutations.createMySpaceMembership,
    variables: doc,
  });
  return response.data.createMySpaceMembership;
};

export const createPreactivatedSpaceMembershipOperation = async (
  spaceID,
  referrer,
) => {
  const response = await client.graphql({
    query: mutations.createMySpaceMembership,
    variables: {
      spaceID,
      isPreactivated: true,
      referrer,
    },
  });
  return response.data.createMySpaceMembership;
};

export const updateMembershipWithAppleIAPOperation = async (
  spaceMembershipID,
  appStoreTransactionReceipt,
) => {
  const response = await client.graphql({
    query: mutations.updateMembershipWithAppleIAP,
    variables: {
      spaceMembershipID,
      appStoreTransactionReceipt,
    },
  });
  return response.data.updateMembershipWithAppleIAP;
};

export const updateMembershipWithGooglePlayPurchaseOperation = async (
  spaceMembershipID,
  googlePlayTransactionReceipt,
) => {
  const response = await client.graphql({
    query: mutations.updateMembershipWithGooglePlayPurchase,
    variables: {
      spaceMembershipID,
      googlePlayTransactionReceipt,
    },
  });
  return response.data.updateMembershipWithGooglePlayPurchase;
};

export const updateMembershipNoTierAvailableOperation = async (
  spaceMembershipID,
) => {
  const response = await client.graphql({
    query: mutations.updateMembershipNoTierAvailable,
    variables: {
      spaceMembershipID,
    },
  });
  return response.data.updateMembershipNoTierAvailable;
};

export const createStripeCheckoutSessionOperation = async (
  membershipTierID,
  spaceMembershipID,
  returnUrl,
  duration,
) => {
  const response = await client.graphql({
    query: mutations.createStripeCheckoutSession,
    variables: {
      membershipTierID,
      spaceMembershipID,
      returnUrl,
      duration,
    },
  });
  return response.data.createStripeCheckoutSession;
};

export const checkStripeCheckoutSessionOperation = async (
  spaceMembershipID,
) => {
  const response = await client.graphql({
    query: mutations.checkStripeCheckoutSession,
    variables: {
      spaceMembershipID,
    },
  });
  return response.data.checkStripeCheckoutSession;
};

// export const emailWelcomeToSpaceMessageOperation = async (
//   recipient,
//   givenName,
//   familyName,
//   spaceTitle,
//   spaceSubscribeUrl,
// ) => {
//   const response = await client.graphql({
//     query: mutations.emailWelcomeToSpaceMessage,
//     variables: {
//       recipient,
//       givenName,
//       familyName,
//       spaceTitle,
//       spaceSubscribeUrl,
//     },
//   });
//   return response.data.emailWelcomeToSpaceMessage;
// };

export const cancelSubscriptionOperation = async (spaceMembershipID) => {
  const response = await client.graphql({
    query: mutations.cancelSubscription,
    variables: {
      spaceMembershipID,
    },
  });
  return response.data.cancelSubscription;
};

const SIGNALS_EXPIRY_MINUTES = 15;

export const getLatestAccountActivitySignalsOperation = async (spaceID) => {
  const nowStr = new Date().toISOString();
  const oldestSignalTimestampStr = dayjs(nowStr)
    .subtract(SIGNALS_EXPIRY_MINUTES, 'minute')
    .toISOString();
  const signals = [];
  const fetchPage = async (nextToken) => {
    const signalsResponse = await client.graphql({
      query: queries.accountActivitySignalBySpaceID,
      variables: {
        spaceID,
        lastActivityAt: { gt: oldestSignalTimestampStr },
        nextToken,
      },
    });
    const signalsPage =
      signalsResponse.data?.accountActivitySignalBySpaceID?.items || [];
    signals.push(...signalsPage);
    return signalsResponse.data?.accountActivitySignalBySpaceID?.nextToken;
  };

  let nextToken = null;
  do {
    // eslint-disable-next-line no-await-in-loop
    nextToken = await fetchPage(nextToken);
  } while (nextToken !== null);

  return signals;
};

export const pingRecipesServiceOperation = async () => {
  const response = await client.graphql({
    query: mutations.pingRecipesService,
  });
  return response.data.pingRecipesService;
};

export const reportAccountActivitySignalOperation = async (
  accountActivitySignal,
) => {
  const response = await client.graphql({
    query: mutations.reportAccountActivitySignal,
    variables: {
      accountActivitySignal,
    },
  });
  return response.data.reportAccountActivitySignal;
};

export const createStripeBillingPortalSessionOperation = async (
  spaceMembershipID,
  returnUrl,
) => {
  const response = await client.graphql({
    query: mutations.createStripeBillingPortalSession,
    variables: {
      spaceMembershipID,
      returnUrl,
    },
  });
  return response.data.createStripeBillingPortalSession;
};

export const updateMembershipWithAppleIAP = async (
  purchase,
  currentSpaceMembership,
  dispatch,
) => {
  const { transactionReceipt } = purchase;
  if (!currentSpaceMembership) {
    dispatch(iapSubscriptionProcessingDeferred(purchase));
    return;
  }
  dispatch(iapSubscriptionProcessingResumed(purchase));
  trackEvents([
    {
      name: 'Subscription purchased',
      args: {
        channel: 'apple_iap',
      },
    },
  ]);
  const updateMembershipResponse = await updateMembershipWithAppleIAPOperation(
    currentSpaceMembership.id,
    transactionReceipt,
  );
  console.log({ updateMembershipResponse });
  if (updateMembershipResponse.result === UpdateMembershipResult.OK) {
    dispatch(appSyncGraphQLApi.util.invalidateTags(['SpaceMembership']));
    postMessageToSmorgCompanion({
      type: 'spaceMembershipUpdated',
      purchase,
    });
    const patchedSpaceMembership =
      spaceMembershipWithBackendUpdateMembershipResult(
        currentSpaceMembership,
        updateMembershipResponse,
      );
    dispatch(
      appSyncGraphQLApi.util.updateQueryData(
        'getSpaceMembershipsByMemberID',
        currentSpaceMembership.memberID,
        (draft) => {
          const index = draft.findIndex(
            (m) => m.id === currentSpaceMembership.id,
          );
          if (index !== -1) {
            // eslint-disable-next-line no-param-reassign
            draft[index] = patchedSpaceMembership;
          }
        },
      ),
    );
  }
};

export const updateMembershipWithGooglePlayPurchase = async (
  purchase,
  currentSpaceMembership,
  dispatch,
) => {
  const { transactionReceipt: legacyTransactionReceipt, dataAndroid } = purchase;
  const transactionReceipt = dataAndroid || legacyTransactionReceipt;
  if (!currentSpaceMembership) {
    dispatch(iapSubscriptionProcessingDeferred(purchase));
    return;
  }
  dispatch(iapSubscriptionProcessingResumed(purchase));
  trackEvents([
    {
      name: 'Subscription purchased',
      args: {
        channel: 'google_play_purchase',
      },
    },
  ]);
  const updateMembershipResponse =
    await updateMembershipWithGooglePlayPurchaseOperation(
      currentSpaceMembership.id,
      transactionReceipt,
    );
  console.log({ updateMembershipResponse });
  if (updateMembershipResponse.result === UpdateMembershipResult.OK) {
    dispatch(appSyncGraphQLApi.util.invalidateTags(['SpaceMembership']));
    postMessageToSmorgCompanion({
      type: 'spaceMembershipUpdated',
      purchase,
    });
    const patchedSpaceMembership =
      spaceMembershipWithBackendUpdateMembershipResult(
        currentSpaceMembership,
        updateMembershipResponse,
      );
    dispatch(
      appSyncGraphQLApi.util.updateQueryData(
        'getSpaceMembershipsByMemberID',
        currentSpaceMembership.memberID,
        (draft) => {
          const index = draft.findIndex(
            (m) => m.id === currentSpaceMembership.id,
          );
          if (index !== -1) {
            // eslint-disable-next-line no-param-reassign
            draft[index] = patchedSpaceMembership;
          }
        },
      ),
    );
  }
};

export const activateSpaceOperation = async (spaceID) => {
  const response = await client.graphql({
    query: mutations.activateSpace,
    variables: { spaceID },
  });
  return response.data.activateSpace;
};
